import type { FC } from 'react';
import { Flex, HStack, VStack } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { TimeIcon } from '../../../../../components/icons';
import { SoftLink } from '../../../../../components/soft-link/SoftLink';
import { Button, Modal, Text } from '../../../../../ui-components';
import { PageUrl } from '../../../../utils/page-url';

interface ResetPasswordErrorModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ResetPasswordErrorModal: FC<ResetPasswordErrorModalProps> = ({ isOpen, onClose }) => (
    <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Something Went Wrong..."
        id="account-reset-password-error-modal"
        size="extended"
        containerStyle={{ padding: { base: '0 1rem 2rem 1rem', sm: '0 1rem 1rem 1rem' } }}
    >
        <VStack spacing={8} width="100%" align="start">
            <VStack spacing={4} align="start">
                <HStack spacing={1} align="start">
                    <TimeIcon width={6} height={6} p={1} />
                    <Text style="bold" id="account-reset-password-error-title">
                        <FormattedMessage
                            id="account.reset-password.error.link_expired_or_used"
                            defaultMessage="This link has expired or has already been used."
                        />
                    </Text>
                </HStack>
                <VStack spacing={1} align="start">
                    <Text id="account-reset-password-error-message-1">
                        <FormattedMessage
                            id="account.reset-password.error.links_can_only_be_used_once"
                            defaultMessage="Links can only be used once, and they expire after 10 minutes."
                        />
                    </Text>
                    <Text id="account-reset-password-error-message-2">
                        <FormattedMessage
                            id="account.reset-password.error.submit_another_request"
                            defaultMessage="Please submit another password reset request below."
                        />
                    </Text>
                </VStack>
            </VStack>

            <Flex direction={{ base: 'column', sm: 'row' }} gap={4} width="100%">
                <SoftLink
                    to={PageUrl.HOME}
                    className="w-full"
                    data-testid="account-reset-password-home-link"
                >
                    <Button
                        variant="secondary"
                        wide
                        id="account-reset-password-continue-shopping-button"
                    >
                        <FormattedMessage
                            id="account.reset-password.continue_shopping"
                            defaultMessage="Continue Shopping"
                        />
                    </Button>
                </SoftLink>
                <SoftLink
                    to={PageUrl.ACCOUNT_FORGOT_PASSWORD}
                    className="w-full"
                    data-testid="account-reset-password-forgot-link"
                >
                    <Button
                        variant="primary-black"
                        wide
                        id="account-reset-password-forgot-password-button"
                    >
                        <FormattedMessage
                            id="account.reset-password.reset_password"
                            defaultMessage="Reset Password"
                        />
                    </Button>
                </SoftLink>
            </Flex>
        </VStack>
    </Modal>
);
